import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { useId } from 'react';
import { FOCUS_RING, MOTION_COLORS } from '../internal/classes';
import { cx } from '../internal/cx';

/**
 * The box is 16 px so it sits on the text baseline, and a pseudo-element grows the pointer target to
 * 24 px, or 44 px where the pointer is coarse (WCAG 2.2 AA, 2.5.8).
 */
const HIT_AREA =
  "relative after:absolute after:-inset-1 after:content-[''] pointer-coarse:after:-inset-3.5";

/** Props of {@link Checkbox}. */
export interface CheckboxProps {
  /** Controlled state. Leave it out and use `defaultChecked` for an uncontrolled checkbox. */
  checked?: boolean;
  /** Initial state of an uncontrolled checkbox. */
  defaultChecked?: boolean;
  /**
   * Shows the mixed state (a dash) and reports `aria-checked="mixed"`. Used by a "select all" box
   * over a partly selected list; `checked` is then ignored.
   */
  indeterminate?: boolean;
  /** Called with the new state. The mixed state always resolves to checked when the reader clicks. */
  onCheckedChange?: (checked: boolean) => void;
  /** Visible label, in the reader's language. */
  label: string;
  /** Extra line under the label explaining what the option does. */
  description?: string;
  /** Keeps the label for assistive technology but takes it out of the layout. */
  hideLabel?: boolean;
  /** Greys the control out and stops interaction. */
  disabled?: boolean;
  /** Field name, when the checkbox is submitted inside a form. */
  name?: string;
  /** Field value, when the checkbox is submitted inside a form. */
  value?: string;
  /** Own id; generated with `useId` when absent. */
  id?: string;
  /** Extra utility classes for the row. */
  className?: string;
}

/**
 * A labelled checkbox with an optional mixed state.
 *
 * @example
 * <Checkbox
 *   label={t('settings.conversationView')}
 *   description={t('settings.conversationViewHint')}
 *   checked={grouped}
 *   onCheckedChange={setGrouped}
 * />
 */
export function Checkbox({
  checked,
  defaultChecked,
  indeterminate = false,
  onCheckedChange,
  label,
  description,
  hideLabel = false,
  disabled = false,
  name,
  value,
  id: idProp,
  className,
}: CheckboxProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const descriptionId = `${id}-description`;

  return (
    <div className={cx('flex items-start gap-2', className)}>
      <CheckboxPrimitive.Root
        id={id}
        name={name}
        value={value}
        disabled={disabled}
        checked={indeterminate ? 'indeterminate' : checked}
        defaultChecked={defaultChecked}
        aria-describedby={description === undefined ? undefined : descriptionId}
        onCheckedChange={(state) => onCheckedChange?.(state === true)}
        className={cx(
          'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-xs border',
          'border-border-strong bg-surface text-text-on-brand cursor-pointer',
          'data-[state=checked]:border-brand data-[state=checked]:bg-brand',
          'data-[state=indeterminate]:border-brand data-[state=indeterminate]:bg-brand',
          'disabled:cursor-not-allowed disabled:opacity-50',
          HIT_AREA,
          FOCUS_RING,
          MOTION_COLORS,
        )}
      >
        <CheckboxPrimitive.Indicator className="flex items-center justify-center">
          <svg viewBox="0 0 12 12" className="size-3" fill="none" aria-hidden="true">
            {indeterminate ? (
              <path d="M2.5 6h7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            ) : (
              <path
                d="M2 6.2 4.7 9 10 3.4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
          </svg>
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      <div className="flex flex-col">
        <label
          htmlFor={id}
          className={cx(
            'text-subhead text-text',
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
            hideLabel && 'sr-only',
          )}
        >
          {label}
        </label>
        {description !== undefined && (
          <p id={descriptionId} className="text-caption text-text-secondary">
            {description}
          </p>
        )}
      </div>
    </div>
  );
}
